import { createContext, useContext, useState } from 'react'
import AuthTokens from '@interfaces/auth.interface'
import { localStorage } from '@services/local-storage.service'
import { StorageKeys } from '@constants/storage-keys.constant'

interface AuthContextType {
  authTokens: AuthTokens | null
  setAuthTokens: (tokens: AuthTokens | null) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authTokens, setAuthTokensState] = useState<AuthTokens | null>(() => {
    const stored = localStorage.getItem<AuthTokens>(StorageKeys.AUTH_TOKENS)
    return stored || null
  })

  /**
   * Set the auth tokens in the context
   * @param tokens - The auth tokens to set
   */
  const setAuthTokens = (tokens: AuthTokens | null) => {
    setAuthTokensState(tokens)
    if (tokens) {
      localStorage.setItem(StorageKeys.AUTH_TOKENS, tokens)
    } else {
      localStorage.removeItem(StorageKeys.AUTH_TOKENS)
    }
  }

  /**
   * Use the auth context
   * @returns The auth context
   */
  return <AuthContext.Provider value={{ authTokens, setAuthTokens }}>{children}</AuthContext.Provider>
}

/**
 * Use the auth context
 * @returns The auth context
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
